import { useState, useMemo } from 'react';
import { PageHeader } from '@/components/shared/page-header';
import { StatusBadge } from '@/components/shared/status-badge';
import { usePurchaseOrder } from '@/hooks/use-purchase-orders';
import { useWarehouseEditSaleOrder, usePurchaseOrderEdits } from '@/hooks/use-warehouses';
import { toast } from '@/stores/toast-store';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { History, Save, Printer } from 'lucide-react';
import {
  aisleOptions,
  buildingOptions,
  filterByLocation,
  locationFilterQuery,
  sortByLocation,
  type LocationFilter,
  type SaleOrderLine,
} from './sale-order-locations';
import { isZeroQty, ZERO_QTY_ROW_CLASS } from './zero-qty';
import { ZeroQtyTag } from './zero-qty-tag';
import { usePermissions } from '@/hooks/use-permissions';

interface SaleOrderEditPageProps {
  orderId: string;
}

export function SaleOrderEditPage({ orderId }: SaleOrderEditPageProps) {
  const { data: orderData, isLoading } = usePurchaseOrder(orderId);
  const { data: editsData } = usePurchaseOrderEdits(orderId);
  const editMut = useWarehouseEditSaleOrder();

  const order = orderData?.data;
  // After migration 0049 the legacy 'generated' status was renamed to 'sent'.
  // Changing quantities needs Edit quantities; anyone else sees it read-only.
  const canEdit = usePermissions().has('sales_orders.edit');
  const editable = order?.status === 'sent' && canEdit;

  // Only user overrides are kept in state; initial values come from the order.
  // Keyed by `${updatedAt}:${itemId}` so a save (which bumps updatedAt) naturally
  // clears out stale overrides.
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  // Building / Aisle filters only hide rows — edits on hidden rows are kept
  // and Save still covers the whole order.
  const [filter, setFilter] = useState<LocationFilter>({ building: '', aisle: '' });
  const orderKey = order?.updatedAt ?? '';
  const setQty = (itemId: string, value: string) =>
    setOverrides((prev) => ({ ...prev, [`${orderKey}:${itemId}`]: value }));
  const getQty = (itemId: string, fallback: string | number) => overrides[`${orderKey}:${itemId}`] ?? String(fallback);

  const dirty = useMemo(() => {
    if (!order?.items) return false;
    return order.items.some((it: any) => {
      const v = overrides[`${orderKey}:${it.id}`];
      return v !== undefined && Number(v) !== Number(it.quantity);
    });
  }, [overrides, orderKey, order]);

  // Cost / total are hidden in the SO (warehouse) view per buyer request —
  // warehouse staff only edit quantity. Helpers stripped along with the
  // columns to keep the file lint-clean.

  const handleSave = async () => {
    if (!order) return;
    // 0 is allowed (line kept, shown struck through); a blank field is
    // skipped rather than read as 0.
    const diff = order.items
      .map((it: any) => ({ id: it.id, raw: getQty(it.id, it.quantity).trim() }))
      .filter((x: any) => x.raw !== '' && Number.isFinite(Number(x.raw)) && Number(x.raw) >= 0)
      .map((x: any) => ({ id: x.id, quantity: Number(x.raw) }));
    try {
      await editMut.mutateAsync({ id: orderId, items: diff });
      toast.success('Sale order updated');
    } catch (err: any) {
      const msg = err?.error?.message || 'Failed to update sale order';
      toast.error(msg);
    }
  };

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>;
  }
  if (!order) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground">Sale order not found.</div>;
  }

  const edits = editsData?.data ?? [];
  const allItems = sortByLocation(order.items as SaleOrderLine[]);
  const visibleItems = filterByLocation(allItems, filter);
  const isFiltered = !!(filter.building || filter.aisle);

  // Opens the print view carrying the current filters, so staff can print
  // just one building / aisle. The tab name includes the filter: re-opening a
  // named tab at a different hash doesn't reload it, so one tab per filter
  // keeps each printout's filter (and its auto print dialog) correct.
  const handlePrint = () => {
    const query = locationFilterQuery(filter);
    window.open(`#/pos-management/sale-orders/${orderId}/print${query}`, `so-print-${orderId}${query}`);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Sale Order #${order.orderNumber}`}
        backTo="/pos-management?tab=warehouse-outgoing"
        backLabel="Back to outgoing orders"
      >
        <Button
          type="button"
          variant="secondary"
          onClick={handlePrint}
          disabled={dirty}
          title={dirty ? 'Save your quantity changes before printing' : undefined}
          className="gap-2"
        >
          <Printer className="h-4 w-4" />
          Print
        </Button>
      </PageHeader>

      {/* Summary */}
      <div className="rounded-lg border border-border bg-white p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <div className="text-xs text-muted-foreground">Destination</div>
            <div className="mt-1 text-sm font-medium">{order.location?.name || order.locationName || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Ordered By</div>
            <div className="mt-1 text-sm font-medium">{order.createdByName || 'System'}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Status</div>
            <div className="mt-1">
              <StatusBadge status={order.status} />
            </div>
          </div>
        </div>
        {!editable && (
          <div className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
            {order.status === 'sent'
              ? 'You can view this order but not change its quantities.'
              : `This order is ${order.status} and can no longer be edited.`}
          </div>
        )}
      </div>

      {/* Line items */}
      <div className="rounded-lg border border-border bg-white p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-base font-semibold">Line Items</h3>
            {isFiltered && (
              <p className="text-xs text-muted-foreground">
                Showing {visibleItems.length} of {allItems.length} items
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SearchableSelect
              className="w-44"
              value={filter.building}
              onChange={(v) => setFilter({ building: v, aisle: '' })}
              options={buildingOptions(allItems)}
              placeholder="All buildings"
              searchPlaceholder="Search buildings..."
            />
            <SearchableSelect
              className="w-44"
              value={filter.aisle}
              onChange={(v) => setFilter((f) => ({ ...f, aisle: v }))}
              options={aisleOptions(allItems, filter.building)}
              placeholder="All aisles"
              searchPlaceholder="Search aisles..."
            />
            {isFiltered && (
              <Button type="button" size="sm" variant="ghost" onClick={() => setFilter({ building: '', aisle: '' })}>
                Clear
              </Button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="pb-2">Product</th>
                <th className="pb-2">Barcode</th>
                <th className="pb-2">Building</th>
                <th className="pb-2">Aisle</th>
                <th className="pb-2">Bin/Bay</th>
                <th className="pb-2">U/M</th>
                <th className="pb-2">Qty</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                    No line items match the selected building and aisle.
                  </td>
                </tr>
              )}
              {visibleItems.map((it) => {
                const zero = isZeroQty(getQty(it.id, it.quantity));
                return (
                  <tr key={it.id} className={`border-b border-border ${zero ? ZERO_QTY_ROW_CLASS : ''}`}>
                    <td className="py-2">
                      {it.productName}
                      {zero && <ZeroQtyTag />}
                    </td>
                    <td className="py-2 text-muted-foreground">{it.productBarcode || '—'}</td>
                    <td className="py-2">{it.productBuilding || '—'}</td>
                    <td className="py-2">{it.productAisle || '—'}</td>
                    <td className="py-2">{it.productBin || '—'}</td>
                    <td className="py-2">{it.unitOfMeasure}</td>
                    <td className="py-2">
                      {editable ? (
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={getQty(it.id, it.quantity)}
                          onChange={(e) => setQty(it.id, e.target.value)}
                          className="h-9 w-24 rounded-md border border-input bg-background px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      ) : (
                        <span>{it.quantity}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {editable && (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || editMut.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {editMut.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Audit history */}
      <div className="rounded-lg border border-border bg-white p-6">
        <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
          <History className="h-4 w-4 text-muted-foreground" />
          Edit History
        </h3>
        {edits.length === 0 ? (
          <div className="text-sm text-muted-foreground">No edits yet.</div>
        ) : (
          <div className="space-y-4">
            {edits.map((e: any) => (
              <div key={e.id} className="border-l-2 border-primary/30 pl-3">
                <div className="text-xs text-muted-foreground">
                  {new Date(e.createdAt).toLocaleString()} · {e.editedByName || 'Warehouse'} ({e.editedFrom})
                </div>
                <ul className="mt-1 space-y-0.5 text-sm">
                  {(e.changes?.items ?? []).map((c: any, i: number) => (
                    <li key={i}>
                      <span className="font-medium">{c.productName}</span>:{' '}
                      <span className="text-muted-foreground">{c.oldQty}</span>
                      <span className="mx-1 text-muted-foreground">→</span>
                      <span className="font-medium">{c.newQty}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
